import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon, Clock, MapPin, Trophy, Users, 
  Plus, Check, Star, Download, ChevronRight, Share2, 
  Server, Shield, Filter, Sparkles, X, CheckCircle2
} from "lucide-react";
import FaIcon from "../components/FaIcon";

export default function EventsCalendar() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [gameFilter, setGameFilter] = useState("all");
  
  // Modal Create
  const [createModal, setCreateModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    category: "esports",
    game: "cs16",
    start_date: "",
    venue: "INDUNGI Arena Server",
    server_ip: "209.38.247.243:27015",
    prize_pool: "100 EUR + VIP",
    banner_url: ""
  });

  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/calendar/events?category=${categoryFilter}&game=${gameFilter}`);
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [categoryFilter, gameFilter]);

  const handleRSVP = async (eventId, status) => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru RSVP!");
      return;
    }
    try {
      await api.post(`/calendar/events/${eventId}/rsvp`, { status });
      toast.success(status === "going" ? "Ai confirmat participarea!" : status === "interested" ? "Ai marcat ca interesat!" : "Participare anulată");
      loadEvents();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newEvent.title.trim() || !newEvent.start_date) {
      toast.error("Completați titlul și data evenimentului!");
      return;
    }
    try {
      await api.post("/calendar/events", newEvent);
      toast.success("Eveniment adăugat în calendar!");
      setCreateModal(false);
      loadEvents();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDownloadICal = (eventId) => {
    window.open(`${api.defaults.baseURL}/calendar/events/${eventId}/ical`, "_blank");
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8" data-testid="events-calendar-page">
      {/* Header Banner */}
      <div className="relative overflow-hidden bg-[#070a10] border border-[#141a29] p-6 sm:p-8 rounded-xl shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--tf-primary)]/10 blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-[10px] font-mono uppercase font-bold bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border border-[var(--tf-primary)]/40 rounded">
                ESPORTS & COMMUNITY SCHEDULE
              </span>
              <span className="text-[10px] font-mono text-zinc-500">• LIVE SYNC</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-white font-chakra uppercase tracking-tight">
              CALENDAR EVENIMENTE & CUPA INDUNGI
            </h1>
            <p className="text-xs sm:text-sm text-zinc-400 font-mono max-w-2xl">
              Programul oficial al meciurilor de Counter-Strike 1.6 & CS2, nopților de mix 5v5, turneelor comunității și evenimentelor speciale.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <button
                onClick={() => setCreateModal(true)}
                className="tf-btn-primary px-5 py-2.5 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 shadow-[0_0_20px_rgba(255,79,0,0.3)] rounded"
              >
                <Plus className="w-4 h-4" />
                <span>Propune Eveniment</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#080c14] border border-[#141a29] p-4 rounded-xl shadow-md">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-zinc-400 font-bold uppercase flex items-center gap-1.5 mr-2">
            <Filter className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
            <span>Categorie:</span>
          </span>
          {[
            { id: "all", label: "Toate" },
            { id: "esports", label: "Esports & Meciuri" },
            { id: "tournament", label: "Turnee Oficiale" },
            { id: "server_event", label: "Evenimente Servere" },
            { id: "community", label: "Comunitate" }
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-3 py-1.5 text-xs font-chakra font-bold uppercase tracking-wider transition-all rounded ${
                categoryFilter === cat.id
                  ? "bg-[var(--tf-primary)] text-black shadow-md font-black"
                  : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border border-white/5"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-zinc-400 font-bold uppercase">Joc:</span>
          {["all", "cs16", "cs2"].map((g) => (
            <button
              key={g}
              onClick={() => setGameFilter(g)}
              className={`px-3 py-1 text-xs font-mono font-bold uppercase rounded ${
                gameFilter === g ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/50" : "bg-white/5 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {g === "all" ? "Toate" : g === "cs16" ? "CS 1.6" : "CS2"}
            </button>
          ))}
        </div>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="p-16 text-center text-xs font-mono text-zinc-500">Se încarcă calendarul...</div>
      ) : events.length === 0 ? (
        <div className="p-16 text-center bg-[#080c14] border border-[#141a29] rounded-xl space-y-3">
          <CalendarIcon className="w-12 h-12 text-zinc-600 mx-auto opacity-50" />
          <h3 className="text-sm font-chakra font-bold text-white uppercase">Nu există evenimente programate</h3>
          <p className="text-xs font-mono text-zinc-500">Fii primul care propune un turneu sau un meci de noapte!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {events.map((ev) => {
            const startDate = new Date(ev.start_date);
            const myRsvp = ev.rsvps?.[user?.id]?.status;

            return (
              <div
                key={ev.id}
                className="bg-[#080c14] border border-[#161f30] hover:border-[var(--tf-primary)]/40 transition-all rounded-xl overflow-hidden shadow-xl flex flex-col justify-between group"
              >
                {/* Banner / Cover */}
                {ev.banner_url && (
                  <div className="relative h-44 w-full overflow-hidden bg-black/50">
                    <img
                      src={ev.banner_url}
                      alt={ev.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#080c14] via-transparent to-black/40" />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[9px] font-mono uppercase font-bold bg-black/70 backdrop-blur-md text-amber-300 border border-amber-500/40 rounded">
                        {ev.category}
                      </span>
                      <span className="px-2 py-0.5 text-[9px] font-mono uppercase font-bold bg-black/70 backdrop-blur-md text-cyan-300 border border-cyan-500/40 rounded">
                        {ev.game === "cs16" ? "CS 1.6" : ev.game === "cs2" ? "CS2" : "ALL GAMES"}
                      </span>
                    </div>
                  </div>
                )}

                <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      {/* Date Badge */}
                      <div className="shrink-0 w-14 h-14 bg-[#0d1320] border border-[#1e273d] flex flex-col items-center justify-center rounded-lg text-center shadow-inner">
                        <span className="text-[10px] font-mono uppercase font-black text-[var(--tf-primary)]">
                          {startDate.toLocaleString("ro-RO", { month: "short" })}
                        </span>
                        <span className="text-xl font-black text-white font-chakra">
                          {startDate.getDate()}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-chakra font-black uppercase text-white tracking-wide group-hover:text-[var(--tf-primary)] transition-colors line-clamp-1">
                          {ev.title}
                        </h3>
                        <div className="flex items-center gap-3 text-[11px] font-mono text-zinc-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-zinc-500" />
                            <span>{startDate.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}</span>
                          </span>
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3.5 h-3.5 text-zinc-500" />
                            <span>{ev.venue}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs font-mono text-zinc-400 line-clamp-2 leading-relaxed">
                      {ev.description}
                    </p>

                    {/* Metadata Strip */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5 text-[11px] font-mono">
                      {ev.prize_pool && (
                        <div className="flex items-center gap-1.5 text-amber-300">
                          <Trophy className="w-3.5 h-3.5 text-amber-400" />
                          <span className="truncate">{ev.prize_pool}</span>
                        </div>
                      )}
                      {ev.server_ip && (
                        <div className="flex items-center gap-1.5 text-cyan-300">
                          <Server className="w-3.5 h-3.5 text-cyan-400" />
                          <span className="truncate">{ev.server_ip}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attendees & RSVP Toolbar */}
                  <div className="pt-4 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                      <Users className="w-4 h-4 text-emerald-400" />
                      <span className="font-bold text-white">{ev.going_count || 0}</span>
                      <span>participanți</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownloadICal(ev.id)}
                        className="p-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white border border-white/10 rounded transition-colors"
                        title="Exportă în Google / Apple Calendar (.ics)"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>

                      {myRsvp === "going" ? (
                        <button
                          onClick={() => handleRSVP(ev.id, "declined")}
                          className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-1.5 rounded"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Participi</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRSVP(ev.id, "going")}
                          className="px-4 py-1.5 bg-[var(--tf-primary)] hover:brightness-110 text-black text-xs font-chakra font-black uppercase tracking-wider transition-all shadow-md rounded"
                        >
                          Participă
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE EVENT MODAL */}
      {createModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={handleCreate} className="bg-[#0b0e15] border border-white/15 p-6 max-w-lg w-full space-y-4 shadow-2xl rounded-xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-chakra font-black uppercase text-white flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-[var(--tf-primary)]" />
                <span>Adaugă Eveniment în Calendar</span>
              </h3>
              <button type="button" onClick={() => setCreateModal(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs font-mono max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <label className="text-zinc-400 block mb-1">Titlu Eveniment *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Noaptea de Mix 5v5 CS 1.6"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 block mb-1">Categorie</label>
                  <select
                    value={newEvent.category}
                    onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                  >
                    <option value="esports">Esports & Meciuri</option>
                    <option value="tournament">Turneu Oficial</option>
                    <option value="server_event">Eveniment Server</option>
                    <option value="community">Comunitate</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 block mb-1">Joc</label>
                  <select
                    value={newEvent.game}
                    onChange={(e) => setNewEvent({ ...newEvent, game: e.target.value })}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                  >
                    <option value="cs16">Counter-Strike 1.6</option>
                    <option value="cs2">Counter-Strike 2</option>
                    <option value="all">Toate Jocurile</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Data & Ora de Început *</label>
                <input
                  type="datetime-local"
                  required
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Locație / Server</label>
                <input
                  type="text"
                  placeholder="Ex: INDUNGI Mix Arena #1"
                  value={newEvent.venue}
                  onChange={(e) => setNewEvent({ ...newEvent, venue: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 block mb-1">IP Server</label>
                  <input
                    type="text"
                    placeholder="209.38.247.243:27015"
                    value={newEvent.server_ip}
                    onChange={(e) => setNewEvent({ ...newEvent, server_ip: e.target.value })}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 block mb-1">Premii / Prize Pool</label>
                  <input
                    type="text"
                    placeholder="100 EUR + VIP"
                    value={newEvent.prize_pool}
                    onChange={(e) => setNewEvent({ ...newEvent, prize_pool: e.target.value })}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                  />
                </div>
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Banner Image URL</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={newEvent.banner_url}
                  onChange={(e) => setNewEvent({ ...newEvent, banner_url: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>

              <div>
                <label className="text-zinc-400 block mb-1">Descriere & Detalii</label>
                <textarea
                  rows="3"
                  placeholder="Regulament meci, format, cerințe..."
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-white rounded"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button type="button" onClick={() => setCreateModal(false)} className="px-4 py-2 bg-white/5 text-zinc-400 hover:text-white text-xs font-chakra uppercase font-bold rounded">
                Anulează
              </button>
              <button type="submit" className="px-5 py-2 bg-[var(--tf-primary)] text-black text-xs font-chakra font-black uppercase rounded shadow-lg">
                Publică Eveniment
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
